import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  adminGetStats,
  adminGetUsers,
  adminGetCompanies,
  adminGetFunders,
  adminGetApplications,
  adminPushToFunder,
} from '@/lib/api';
import {
  Shield,
  Users,
  Building2,
  Landmark,
  FileText,
  Bell,
  LogOut,
  TrendingUp,
  DollarSign,
  Target,
  Brain,
  Menu,
  X,
  RefreshCw,
  Send,
  BarChart3,
  PieChart,
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_creditai-africa/artifacts/q9fyvy5m_ChatGPT%20Image%20Feb%201%2C%202026%2C%2012_34_17%20PM.png";

const NAV_ITEMS = [
  { id: 'dashboard', icon: BarChart3, label: 'Dashboard' },
  { id: 'users', icon: Users, label: 'Users' },
  { id: 'companies', icon: Building2, label: 'Companies' },
  { id: 'funders', icon: Landmark, label: 'Funders' },
  { id: 'applications', icon: FileText, label: 'Applications' },
];

function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [funders, setFunders] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedFunder, setSelectedFunder] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsData, usersData, companiesData, fundersData, appsData] = await Promise.all([
        adminGetStats(),
        adminGetUsers(),
        adminGetCompanies(),
        adminGetFunders(),
        adminGetApplications(),
      ]);
      setStats(statsData);
      setUsers(usersData);
      setCompanies(companiesData);
      setFunders(fundersData);
      setApplications(appsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handlePushToFunder = async (appId) => {
    if (!selectedFunder) {
      toast.error('Please select a funder');
      return;
    }
    try {
      await adminPushToFunder(appId, selectedFunder);
      toast.success('Application pushed to funder!');
      setSelectedApp(null);
      setSelectedFunder('');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push application');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800 h-16 flex items-center justify-between px-4">
        <img src={LOGO_URL} alt="Apaka" className="h-10 brightness-0 invert" />
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 text-white">
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 bottom-0 w-64 bg-slate-900 text-white z-40 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="p-4 border-b border-slate-800">
          <img src={LOGO_URL} alt="Apaka" className="h-12 brightness-0 invert" />
        </div>
        
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-amber-600 flex items-center justify-center">
              <Shield className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{user?.full_name}</div>
              <div className="text-xs text-slate-400">Administrator</div>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id
                  ? 'bg-amber-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="page-title">
                {activeTab === 'dashboard' && 'Admin Dashboard'}
                {activeTab === 'users' && 'User Management'}
                {activeTab === 'companies' && 'Companies'}
                {activeTab === 'funders' && 'Funders'}
                {activeTab === 'applications' && 'Applications'}
              </h1>
              <p className="text-slate-500">Platform administration</p>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={fetchData} variant="outline" size="icon">
                <RefreshCw className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && stats && (
            <div className="space-y-6">
              {/* Platform Stats */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-slate-200" data-testid="stat-users">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Users className="h-4 w-4" />
                      <span className="text-sm">Total Users</span>
                    </div>
                    <div className="text-2xl font-bold">{stats.total_users}</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200" data-testid="stat-companies">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Building2 className="h-4 w-4" />
                      <span className="text-sm">Companies</span>
                    </div>
                    <div className="text-2xl font-bold">{stats.companies}</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200" data-testid="stat-funders">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Landmark className="h-4 w-4" />
                      <span className="text-sm">Funders</span>
                    </div>
                    <div className="text-2xl font-bold">{stats.funders}</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200" data-testid="stat-analyses">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 mb-2">
                      <Brain className="h-4 w-4" />
                      <span className="text-sm">AI Analyses</span>
                    </div>
                    <div className="text-2xl font-bold">{stats.analyses_performed}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Application Stats */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading">Application Pipeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="text-sm text-slate-500">Total Applications</div>
                      <div className="text-2xl font-bold">{stats.applications?.total || 0}</div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-sm text-slate-500">Submitted</div>
                      <div className="text-2xl font-bold text-blue-600">{stats.applications?.submitted || 0}</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="text-sm text-slate-500">Matched</div>
                      <div className="text-2xl font-bold text-purple-600">{stats.applications?.matched || 0}</div>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <div className="text-sm text-slate-500">Funded</div>
                      <div className="text-2xl font-bold text-emerald-600">{stats.applications?.funded || 0}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Lead Stats */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading">Lead Pipeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="text-sm text-slate-500">Total Leads</div>
                      <div className="text-2xl font-bold">{stats.leads?.total || 0}</div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-sm text-slate-500">New</div>
                      <div className="text-2xl font-bold text-blue-600">{stats.leads?.new || 0}</div>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <div className="text-sm text-slate-500">Interested</div>
                      <div className="text-2xl font-bold text-emerald-600">{stats.leads?.interested || 0}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>All Users</CardTitle>
                  <CardDescription>{users.length} registered users</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-slate-500 font-medium">Name</th>
                          <th className="text-left py-3 px-4 text-slate-500 font-medium">Email</th>
                          <th className="text-left py-3 px-4 text-slate-500 font-medium">Role</th>
                          <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                          <th className="text-left py-3 px-4 text-slate-500 font-medium">Tier</th>
                          <th className="text-left py-3 px-4 text-slate-500 font-medium">Credits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-4 px-4 font-medium">{u.full_name}</td>
                            <td className="py-4 px-4 text-slate-600">{u.email}</td>
                            <td className="py-4 px-4">
                              <Badge className={
                                u.role === 'ADMIN' ? 'bg-amber-100 text-amber-700' :
                                u.role === 'FUNDER' ? 'bg-purple-100 text-purple-700' :
                                'bg-blue-100 text-blue-700'
                              }>
                                {u.role}
                              </Badge>
                            </td>
                            <td className="py-4 px-4">
                              <Badge className={u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                                {u.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="py-4 px-4 text-slate-600">{u.subscription_tier}</td>
                            <td className="py-4 px-4 text-slate-600">{u.credits_remaining}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Companies Tab */}
          {activeTab === 'companies' && (
            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>All Companies</CardTitle>
                  <CardDescription>{companies.length} registered companies</CardDescription>
                </CardHeader>
                <CardContent>
                  {companies.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No companies registered yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 text-slate-500 font-medium">Company</th>
                            <th className="text-left py-3 px-4 text-slate-500 font-medium">Country</th>
                            <th className="text-left py-3 px-4 text-slate-500 font-medium">Sector</th>
                            <th className="text-left py-3 px-4 text-slate-500 font-medium">Revenue</th>
                            <th className="text-left py-3 px-4 text-slate-500 font-medium">Verified</th>
                          </tr>
                        </thead>
                        <tbody>
                          {companies.map((c) => (
                            <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-4 px-4 font-medium">{c.company_name}</td>
                              <td className="py-4 px-4 text-slate-600">{c.country}</td>
                              <td className="py-4 px-4 text-slate-600">{c.sector}</td>
                              <td className="py-4 px-4 text-slate-600">
                                {c.annual_revenue ? formatCurrency(c.annual_revenue) : '-'}
                              </td>
                              <td className="py-4 px-4">
                                <Badge className={c.is_verified ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                                  {c.is_verified ? 'Verified' : 'Pending'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Funders Tab */}
          {activeTab === 'funders' && (
            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>All Funders</CardTitle>
                  <CardDescription>{funders.length} registered funders</CardDescription>
                </CardHeader>
                <CardContent>
                  {funders.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <Landmark className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No funders registered yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 text-slate-500 font-medium">Organization</th>
                            <th className="text-left py-3 px-4 text-slate-500 font-medium">Type</th>
                            <th className="text-left py-3 px-4 text-slate-500 font-medium">Country</th>
                            <th className="text-left py-3 px-4 text-slate-500 font-medium">Ticket Size</th>
                            <th className="text-left py-3 px-4 text-slate-500 font-medium">Risk Appetite</th>
                          </tr>
                        </thead>
                        <tbody>
                          {funders.map((f) => (
                            <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-4 px-4 font-medium">{f.organization_name}</td>
                              <td className="py-4 px-4">
                                <Badge variant="outline">{f.funder_type?.replace('_', ' ')}</Badge>
                              </td>
                              <td className="py-4 px-4 text-slate-600">{f.country}</td>
                              <td className="py-4 px-4 text-slate-600">
                                {f.min_ticket_size ? `${formatCurrency(f.min_ticket_size)} - ${formatCurrency(f.max_ticket_size || 0)}` : '-'}
                              </td>
                              <td className="py-4 px-4 text-slate-600">{f.risk_appetite}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div className="space-y-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>All Applications</CardTitle>
                  <CardDescription>{applications.length} total applications</CardDescription>
                </CardHeader>
                <CardContent>
                  {applications.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No applications yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {applications.map((app) => (
                        <div key={app.id} className="p-4 border border-slate-200 rounded-lg">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-bold">{app.company_name}</span>
                                <Badge className={
                                  app.status === 'SUBMITTED' ? 'bg-blue-100 text-blue-700' :
                                  app.status === 'MATCHED' ? 'bg-purple-100 text-purple-700' :
                                  app.status === 'FUNDED' ? 'bg-emerald-100 text-emerald-700' :
                                  'bg-slate-100 text-slate-700'
                                }>
                                  {app.status}
                                </Badge>
                                {app.risk_grade && <Badge variant="outline">{app.risk_grade}</Badge>}
                              </div>
                              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                                <span>{formatCurrency(app.loan_amount, app.currency)}</span>
                                <span>{app.tenor_months} months</span>
                                <span>{app.purpose?.slice(0, 50)}...</span>
                              </div>
                              {app.matched_funders?.length > 0 && (
                                <div className="text-sm text-purple-600 mt-2">
                                  Matched with {app.matched_funders.length} funders
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 items-center">
                              {selectedApp === app.id ? (
                                <>
                                  <Select value={selectedFunder} onValueChange={setSelectedFunder}>
                                    <SelectTrigger className="w-48">
                                      <SelectValue placeholder="Select funder" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {funders.map((f) => (
                                        <SelectItem key={f.id} value={f.id}>{f.organization_name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button onClick={() => handlePushToFunder(app.id)} className="bg-amber-600 hover:bg-amber-700">
                                    <Send className="h-4 w-4" />
                                  </Button>
                                  <Button variant="outline" onClick={() => setSelectedApp(null)}>
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  variant="outline"
                                  onClick={() => setSelectedApp(app.id)}
                                  disabled={funders.length === 0}
                                  data-testid={`push-${app.id}`}
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Push to Funder
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
